import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useEscritorioInfo } from "@/lib/escritorioInfo";
import { toTitleCasePt } from "@/lib/format";
import {
  ARQ_OPCOES, FILTROS_PADRAO, PARADOS_OPTIONS, SIT_OPCOES, tribunalLabel,
} from "@/lib/processosUtil";

// Lote 165 — painel "Filtros" (lateral no computador, de baixo no celular). Nada muda na lista até "Aplicar filtros".
const selectCls = "w-full h-11 px-3 text-sm rounded-md border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-stone-400";
const Rotulo = ({ children }) => <span className="block text-xs font-semibold text-stone-600 mb-1.5">{children}</span>;

export default function FiltrosPainel({
  open, onOpenChange, filtros, responsavel, parados, noEscritorio, escopoEscritorio,
  clients, procCountByClient, areaOptions, tribunalOptions, localOptions, onAplicar, isMobile,
}) {
  const info = useEscritorioInfo(noEscritorio && escopoEscritorio);
  const pessoas = (info?.membros || []).filter((m) => m.papel !== "apoio");
  const extras = info?.ex_membros || [];
  const [f, setF] = useState(filtros);
  const [resp, setResp] = useState(responsavel);
  const [dias, setDias] = useState(parados);
  useEffect(() => {
    if (open) { setF(filtros); setResp(responsavel); setDias(parados); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (k, v) => setF((cur) => ({ ...cur, [k]: v }));
  const toggleSit = (k) => setF((cur) => ({ ...cur, sit: cur.sit.includes(k) ? cur.sit.filter((x) => x !== k) : [...cur.sit, k] }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        data-testid="processos-filters-sheet"
        className={isMobile ? "max-h-[88vh] overflow-y-auto rounded-t-xl" : "overflow-y-auto sm:max-w-md"}
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl">Filtros</SheetTitle>
          <SheetDescription>Escolha e toque em “Aplicar filtros”.</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-5">
          <label className="block">
            <Rotulo>Cliente</Rotulo>
            <select data-testid="processo-filter-client" value={f.cliente} onChange={(e) => set("cliente", e.target.value)} className={selectCls}>
              <option value="all">Todos os clientes</option>
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>{toTitleCasePt(c.name)} ({procCountByClient[c.client_id] || 0})</option>
              ))}
            </select>
          </label>

          {noEscritorio && escopoEscritorio && (
            <label className="block">
              <Rotulo>Responsável</Rotulo>
              <select data-testid="escopo-pessoa" value={resp} onChange={(e) => setResp(e.target.value)} className={selectCls}>
                <option value="">Todos</option>
                {pessoas.map((m) => <option key={m.user_id} value={m.user_id}>{m.nome}{m.eu ? " (você)" : ""}</option>)}
                {extras.map((m) => <option key={m.user_id} value={m.user_id}>{m.nome} (saiu)</option>)}
              </select>
            </label>
          )}

          <label className="block">
            <Rotulo>Tipo de ação</Rotulo>
            <select data-testid="processo-filter-area" value={f.area} onChange={(e) => set("area", e.target.value)} className={selectCls}>
              <option value="all">Todas</option>
              {areaOptions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </label>
          <label className="block">
            <Rotulo>Tribunal</Rotulo>
            <select data-testid="processo-filter-tribunal" value={f.tribunal} onChange={(e) => set("tribunal", e.target.value)} className={selectCls}>
              <option value="all">Todos</option>
              {tribunalOptions.map((t) => <option key={t} value={t}>{tribunalLabel(t)}</option>)}
            </select>
          </label>
          <label className="block">
            <Rotulo>Comarca</Rotulo>
            <select data-testid="processo-filter-local" value={f.local} onChange={(e) => set("local", e.target.value)} className={selectCls}>
              <option value="all">Todas</option>
              {localOptions.map((l) => <option key={l} value={l}>{toTitleCasePt(l)}</option>)}
            </select>
          </label>

          <fieldset>
            <legend className="text-xs font-semibold text-stone-600 mb-1.5">Situação</legend>
            <div className="space-y-1">
              {SIT_OPCOES.filter((o) => !o.soEscritorio || noEscritorio).map((o) => (
                <label key={o.key} className="flex items-center gap-3 min-h-[2.75rem] px-1 text-sm text-stone-800 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`filtro-sit-${o.key}`}
                    checked={f.sit.includes(o.key)}
                    onChange={() => toggleSit(o.key)}
                    className="h-4 w-4 accent-stone-700"
                  />
                  {o.label}
                </label>
              ))}
            </div>
            {f.sit.includes("parados") && (
              <div className="mt-2 ml-1">
                <span className="block text-[11px] text-stone-500 mb-1.5">Sem movimentação capturada há mais de</span>
                <div className="grid grid-cols-4 gap-1.5" role="group" aria-label="Sem movimentação capturada há mais de">
                  {PARADOS_OPTIONS.map((o) => (
                    <button
                      key={o.days}
                      type="button"
                      data-testid={`parados-${o.days}`}
                      aria-pressed={dias === o.days}
                      onClick={() => setDias(o.days)}
                      className={`h-10 rounded-md border text-[13px] font-medium ${dias === o.days ? "bg-stone-700 text-white border-stone-700" : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-stone-500 mt-1.5">Conta desde a última movimentação que o DJEN/DataJud devolveu. Não significa que o processo está parado no tribunal.</p>
              </div>
            )}
          </fieldset>

          <fieldset>
            <legend className="text-xs font-semibold text-stone-600 mb-1.5">Cadastro no app</legend>
            <div className="grid grid-cols-2 gap-1.5" role="group" aria-label="Cadastro no app">
              {ARQ_OPCOES.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  data-testid={`filtro-arq-${o.key}`}
                  aria-pressed={f.arq === o.key}
                  onClick={() => set("arq", o.key)}
                  className={`h-10 rounded-md border text-[13px] font-medium ${f.arq === o.key ? "bg-stone-700 text-white border-stone-700" : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-stone-500 mt-1.5">“Arquivado” pausa a consulta no app; “extinto” é a marca de processo que acabou. Os dois ficam fora da lista normal.</p>
          </fieldset>
        </div>

        <div className="flex gap-2 mt-6 sticky bottom-0 -mx-6 px-6 py-3 bg-white border-t border-stone-200">
          <Button
            type="button"
            data-testid="processos-filters-ver"
            onClick={() => { onAplicar(f, resp, dias); onOpenChange(false); }}
            className="flex-1 bg-stone-700 hover:bg-stone-800 text-white h-11"
          >
            Aplicar filtros
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid="processos-filters-limpar"
            onClick={() => { onAplicar(FILTROS_PADRAO, "", dias); onOpenChange(false); }}
            className="h-11 border-stone-300"
          >
            Limpar filtros
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
